import { HttpContextToken, HttpEvent, HttpInterceptorFn, HttpRequest, HttpResponse } from "@angular/common/http";

import { Observable, tap } from "rxjs";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { isObject } from "@app/kernel/shared/helpers";
import { NewsRoutePaths } from "@app/shared/news/news-route-paths";
import { DialogService } from "primeng/dynamicdialog";

/**
 * HTTP interceptor responsible for handling **GraphQL-level authorization errors**
 * related to restricted news content access.
 *
 * ## Responsibility
 * - Inspects successful `HttpResponse` bodies that contain **GraphQL response arrays**
 * - Detects domain-specific authorization errors returned by the GraphQL API
 * - Redirects the user to the required news article when reading is mandatory
 *
 * ## Why this exists
 * Some GraphQL APIs return authorization errors inside the response body
 * (`errors[]`) instead of rejecting the HTTP request (non-4xx/5xx).
 * This interceptor centralizes that logic to avoid duplicating it across
 * individual API consumers.
 *
 * ## Side Effects
 * - Performs a **router navigation** when a specific GraphQL error code
 *   and message key are detected.
 */
export const newsInterceptor: HttpInterceptorFn = (req, next) => {
  const dialogService = inject(DialogService);
  const router = inject(Router);

  /**
   * Intercepts outgoing HTTP requests and inspects incoming responses.
   *
   * @param req - Outgoing HTTP request
   * @param next - Next interceptor or backend handler
   * @returns Observable stream of HTTP events
   *
   * ## Notes
   * - Only inspects successful `HttpResponse` events
   * - Only processes responses whose body is an array
   *   (as expected from batched GraphQL responses)
   * - Does **not** modify the response stream
   */

  return next(req).pipe(
    tap({
      next: event => {
        if (event instanceof HttpResponse && Array.isArray(event.body)) {
          _handleGraphqlErrors(event.body, router, dialogService);
        }
      },
    })
  );
};

/**
 * Inspects GraphQL response items and handles domain-specific error cases.
 *
 * @param responseItems - Array of GraphQL response objects.
 * Each item may contain an `errors` array following the GraphQL spec.
 *
 * ## Expected GraphQL Error Shape
 * ```ts
 * {
 *   errors: Array<{
 *     extensions?: {
 *       formatted?: {
 *         code?: number;
 *         data?: {
 *           messageKey?: string;
 *         };
 *       };
 *     };
 *   }>;
 * }
 * ```
 *
 * ## Behavior
 * - Ignores non-object and non-error responses
 * - Extracts `extensions.formatted` when available (fallbacks to `extensions`)
 * - Redirects the user when the API signals that news reading is required
 */
const _handleGraphqlErrors = (responseItems: unknown[], router: Router, dialogService: DialogService) => {
  for (const response of responseItems) {
    const gqlErrors = isObject(response) && "errors" in response && response?.errors;
    if (!Array.isArray(gqlErrors)) continue;
    for (const error of gqlErrors) {
      const formatted = error?.extensions?.formatted ?? error?.extensions;
      const { code } = formatted || {};
      /**
       * Domain rule:
       * - HTTP-like 403 code
       * - Specific backend message key indicating mandatory news reading
       */
      if (code === 403 && formatted?.data?.messageKey === "SYSTEM.API_ERROR.NEWS_READING_REQUIRED") {
        // close all open dialogs
        dialogService.dialogComponentRefMap.forEach((_, key) => key.close());
        // navigate to news article route
        router.navigate([NewsRoutePaths.article]);
      }
    }
  }
};
